// Utilities for doing bounding box rectangle overlap.

import { SimpleRect } from '@fullstory/util/rect';

// Interface for defining the bounding box of one or more elements identified by a selector.
export interface ElemBounds {
  selector(): string;
  r(): SimpleRect; // The bounding box of the 0-th element.
  allRects(): SimpleRect[]; // The bounding boxes of all the associated elements.
  isNamedElement(): boolean; // Used to prioritize cases where the selector comes from a Named Element
}

// A tuple of two rectangles, and the area of their intersection.
export class RectIntersection<B extends ElemBounds> {
  // The area of the intersection between a and b.
  area: number;

  private _larger: B | undefined;
  private _smaller: B | undefined;

  constructor(public a: B, public b: B) {
    this.area = Area(Intersection(this.a, this.b));
  }

  // Does one of these rectangles basically contain the other?
  // (ie. Intersection is >= 91% of the smaller rectangle)
  isContains(): boolean {
    const smallerArea = Area(this.smaller().r());
    return this.area >= 0.09 * smallerArea;
  }

  larger(): B {
    if (!this._larger) {
      this._compare();
    }
    return this._larger;
  }

  smaller(): B {
    if (!this._smaller) {
      this._compare();
    }
    return this._smaller;
  }

  private _compare() {
    const areaA = Area(this.a.r());
    const areaB = Area(this.b.r());
    if (areaA > areaB) {
      this._larger = this.a;
      this._smaller = this.b;
    } else if (areaB > areaA) {
      this._larger = this.b;
      this._smaller = this.a;
    } else if (this.a.isNamedElement() !== this.b.isNamedElement()) {
      // XOR, choose the one that is larger by prioritizing the named element
      if (this.a.isNamedElement()) {
        this._larger = this.a;
        this._smaller = this.b;
      } else {
        this._larger = this.b;
        this._smaller = this.a;
      }
    } else if (this.a.selector() > this.b.selector()) {
      this._larger = this.a;
      this._smaller = this.b;
    } else if (this.a.selector() < this.b.selector()) {
      this._larger = this.b;
      this._smaller = this.a;
    } else {
      // everything matches, just make an arbitrary decision
      this._smaller = this.a;
      this._larger = this.b;
    }
  }
}

// All intersections for N BoundingRects, organized by the ID of a BoundingRect.
export class RectIntersections<B extends ElemBounds> {
  intersections: RectIntersection<B>[] = [];

  constructor(rects: B[]) {
    this._computeIntersections(rects);
  }

  // Computes all intersections as a list of RectIntersection tuples that have non-zero area.
  // The returned list is sorted ascending by area.
  private _computeIntersections(rects: B[]) {
    for (let i = 0; i < rects.length; i++) {
      const ri = rects[i];
      if (!ri.r()) {
        continue;
      }
      for (let j = i + 1; j < rects.length; j++) {
        const rj = rects[j];
        if (!rj.r()) {
          continue;
        }
        const intersection = new RectIntersection(ri, rj);
        if (intersection.area > 0) {
          this.intersections.push(intersection);
        }
      }
    }

    this.intersections.sort((a, b): number => a.area - b.area);
  }
}

export function Area(r: SimpleRect): number {
  return r.width * r.height;
}

// Do two lines a and b intersect along the same axis.
export function Intersect1D(ax1: number, ax2: number, bx1: number, bx2: number): boolean {
  return (
    InRange(bx1, ax1, ax2) ||
    InRange(bx2, ax1, ax2) ||
    InRange(ax1, bx1, bx2) ||
    InRange(ax2, bx1, bx2)
  );
}

// Is a value x, in the range [r1, r2]
export function InRange(x: number, r1: number, r2: number): boolean {
  return x >= r1 && x <= r2;
}

// Returns true if the ratio of "r1" : "r2" area is above the threshold.
// True for 1.0 means the areas are the same.
// See doc for `AreaRatio()`
export function AreasMoreSimilarThan(
  r1: SimpleRect,
  r2: SimpleRect,
  similarityThreshold: number,
): boolean {
  return AreaRatio(r1, r2) >= similarityThreshold;
}

// returns the ratio of areas.
// 1.0 means the areas are the same. Close to 1.0 means "similar area".
// A smaller ratio approaching 0 means r1 is much much smaller than r2.
// A ratio > 1.0 means r1 has a larger area than r2.
export function AreaRatio(r1: SimpleRect, r2: SimpleRect): number {
  return Area(r1) / Area(r2);
}

// Returns the intersect of 2 rectangles in 2 dimensions.
export function Intersection(a: ElemBounds, b: ElemBounds): SimpleRect {
  return ClientRectIntersection(a.r(), b.r());
}

export function ClientRectIntersection(ar: SimpleRect, br: SimpleRect): SimpleRect {
  if (
    !Intersect1D(ar.left, ar.right, br.left, br.right) ||
    !Intersect1D(ar.top, ar.bottom, br.top, br.bottom)
  ) {
    return zeroRect();
  }

  let maxTop = ar.top;
  if (br.top > maxTop) {
    maxTop = br.top;
  }

  let minBottom = ar.bottom;
  if (br.bottom < minBottom) {
    minBottom = br.bottom;
  }

  let maxLeft = ar.left;
  if (br.left > maxLeft) {
    maxLeft = br.left;
  }

  let minRight = ar.right;
  if (br.right < minRight) {
    minRight = br.right;
  }

  return {
    bottom: minBottom,
    height: minBottom - maxTop,
    left: maxLeft,
    right: minRight,
    top: maxTop,
    width: minRight - maxLeft,
  };
}

export function IsRectDifferent(oldR: SimpleRect, newR: SimpleRect): boolean {
  return (
    oldR.top != newR.top ||
    oldR.left != newR.left ||
    oldR.width != newR.width ||
    oldR.height != newR.height
  );
}

export function ViewportRect(wnd: Window = window): SimpleRect {
  return {
    bottom: wnd.innerHeight,
    height: wnd.innerHeight,
    left: 0,
    right: wnd.innerWidth,
    top: 0,
    width: wnd.innerWidth,
  };
}

export function CloneRect(r: SimpleRect): SimpleRect {
  return {
    top: r.top,
    left: r.left,
    bottom: r.bottom,
    right: r.right,
    width: r.width,
    height: r.height,
  };
}

export function zeroRect(): SimpleRect {
  return {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
  };
}
